<template>
  <div :class="{ 'account-cellar--my-favorites': !startWithAdd, active: true }">
    <div class="account-cellar--utilities row">
      <div class="shopping columns small-12 medium-4 large-4" />
      <div
        v-if="!startWithAdd"
        class="add-button columns small-12 medium-8 large-8"
      >
        <a href="#" @click.prevent="addSavedSearch">
          <span class="icon-green-add"></span>
          Add Saved Search
        </a>
      </div>
    </div>
    <div class="account-cellar--favorite-container">
      <div class="filter-container" :class="{ active: showAddSearch }">
        <div id="plp-filter-sidebar" class="plp-filter-sidebar">
          <div class="filter-scroller">
            <div class="search">
              <label class="sr-only" for="new-saved-search">
                Search Term
              </label>
              <input
                id="new-saved-search"
                ref="savedSearchInput"
                v-model="searchString"
                type="search"
              />
            </div>
            <FilterAccordions
              v-if="showAddSearch"
              :categories="allCategoryFilters"
              :varietals="allVarietalFilters"
              :food-pairings="allFoodPairingFilters"
              :vintages="vintageFilters"
              :expert-reviewers="expertReviewerFilters"
              :occasion-slugs="occasionSlugs"
              :hide-extra-varietals="hideExtraVarietals"
              @sliderChanged="sliderChanged"
              @sliderCleared="sliderCleared"
              @toggleHideExtraVarietals="toggleHideExtraVarietals"
              @toggleFilter="toggleFilter"
              @updateRegionFilters="updateRegionFilters"
            />
            <div class="plp-filter-footer">
              <div class="row">
                <div class="columns small-6">
                  <button @click="cancelAddSearch">Cancel</button>
                </div>
                <div class="columns small-6">
                  <button class="apply" @click="commitAddSearch">
                    {{ startWithAdd ? 'Create' : 'Save and View' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="account-cellar--favorite-content">
        <div class="favorite-list-group">
          <h2>Saved Searches</h2>
          <div v-if="savedSearchList.length === 0" class="empty">
            Set up customized alerts by region, rating, and vintage to be the
            first in line when new wines arrive.
          </div>
          <ul class="favorite-list">
            <li v-for="search in savedSearchList" :key="search.id">
              <nuxt-link
                :to="`/store/search/?${decodeURIComponent(
                  search.original_query_string,
                )}`"
                class="favorite-name"
              >
                {{ search.name }}
              </nuxt-link>
              <a href="#" @click.prevent="editSavedSearch(search)">
                <span class="icon-pencil"></span>
                <div class="sr-only">Edit</div>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <ProductSavedSearch
      :show="showSaveSearchModal"
      :selected-filters="selectedFilters"
      @saveComplete="handleSaveComplete"
      @close="handleClose"
    />
  </div>
</template>

<script>
import { deslugify } from '#common/utils/slugify'
import { mapGetters } from 'vuex'
import { filterifyMetadata } from '~/utils/filters/filters'

export default {
  name: 'SavedSearchList',
  components: {
    FilterAccordions: () => import('~/components/Product/FilterAccordions.vue'),
    ProductSavedSearch: () =>
      import('~/components/Product/PDP/ProductSavedSearch.vue'),
  },
  props: {
    startWithAdd: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['saveComplete'],
  data() {
    return {
      searchString: '',
      showAddSearch: false,
      hideExtraVarietals: true,
      selectedFilters: [],
      regionsLoading: false,
      showSaveSearchModal: false,
    }
  },
  computed: {
    ...mapGetters([
      'savedSearchList',
      'saveSearchShowModal',
      'savedSearchEditId',
    ]),
    allCategoryFilters() {
      return filterifyMetadata(this.metadata.categories, 'categories')
    },
    allVarietalFilters() {
      const filters = filterifyMetadata(
        Object.values(this.metadata.varietals),
        'varietals',
      )
      if (this.hideExtraVarietals) {
        return filters.filter(
          (v) =>
            this.isFilterSelected(v) ||
            v.show_in_filter_dropdown ||
            !this.hideExtraVarietals,
        )
      }
      return filters
    },
    vintageFilters() {
      const currentYear = this.$moment().year()
      const fiftyYearsAgo = this.$moment().subtract(50, 'years').year()

      const vintages = []
      for (let year = fiftyYearsAgo; year <= currentYear; year++) {
        vintages.push({
          category: 'vintage',
          key: year.toString(),
          name: year.toString(),
        })
      }
      vintages.sort((a, b) => a.name.localeCompare(b.name))

      return vintages
    },
    allFoodPairingFilters() {
      return filterifyMetadata(this.metadata.food, 'food_pairings')
    },
    expertReviewerFilters() {
      return filterifyMetadata(
        this.metadata.expert_reviewers.filter((expert) => expert.filterable),
        'expert_reviewers',
      )
    },
    occasionSlugs() {
      return Object.keys(this.metadata.facetNames.occasions)
    },
  },
  watch: {
    startWithAdd: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.showAddSearch = true
        }
      },
    },
  },
  created() {
    this.metadata = useMetadata()
  },
  methods: {
    addSavedSearch() {
      this.showAddSearch = true
      if (this.regionsLoading) {
        this.startSpinner()
      }
      this.$nextTick(() => {
        this.$refs.savedSearchInput.focus()
      })
    },
    editSavedSearch(search) {
      this.$store.dispatch('setSavedSearchModal', {
        editId: search.id,
        showModal: true,
      })
    },
    isFilterSelected(filter) {
      return !!this.selectedFilters.find((f) => {
        return f.category === filter.category && f.key === filter.key
      })
    },
    cancelAddSearch() {
      this.showAddSearch = false
      this.searchString = ''
      this.selectedFilters = []
      this.metadata.deselectAllRegions()
      this.$emit('saveComplete')
    },
    commitAddSearch() {
      this.selectedFilters = this.selectedFilters.filter(
        (f) => !['search'].includes(f.category),
      )
      this.searchString = this.searchString.trim()
      if (this.searchString) {
        this.selectedFilters.push({
          category: 'search',
          key: this.searchString,
          name: this.searchString,
        })
      }

      this.showSaveSearchModal = true
    },
    toggleHideExtraVarietals() {
      this.hideExtraVarietals = !this.hideExtraVarietals
    },
    toggleFilter(e) {
      if (e.category === 'region_slug') {
        const region = this.metadata.regionsIndex.find(
          (r) => r.chipName === e.name,
        )
        if (region) {
          this.metadata.deselectRegion({ region })
          this.updateRegionFilters()
        }
        return
      }
      const filterIndex = this.selectedFilters.findIndex(
        (f) => f.category === e.category && f.key === e.key,
      )
      if (filterIndex >= 0) {
        this.selectedFilters.splice(filterIndex, 1)
      } else {
        this.selectedFilters.push(e)
      }
    },
    updateRegionFilters() {
      this.selectedFilters = this.selectedFilters
        .filter((f) => f.category !== 'region_slug')
        .concat(
          this.metadata.selectedRegionSlugs.map((slug) => {
            const slugIndex = this.metadata.regionsIndex.find(
              (region) => region.slug === slug,
            )
            return {
              category: 'region_slug',
              key: slug,
              slug,
              name: slugIndex ? slugIndex.name : deslugify(slug),
              chipName: slugIndex ? slugIndex.chipName : deslugify(slug),
            }
          }),
        )
    },
    sliderChanged(newFilter) {
      this.selectedFilters = this.selectedFilters.filter(
        (f) => !(f.category === newFilter.category && f.key === newFilter.key),
      )
      this.selectedFilters.push(newFilter)
    },
    sliderCleared(clearedFilter) {
      this.selectedFilters = this.selectedFilters.filter(
        (f) =>
          !(
            f.category === clearedFilter.category && f.key === clearedFilter.key
          ),
      )
    },
    handleSaveComplete() {
      this.showSaveSearchModal = false
      this.cancelAddSearch()
      this.$emit('saveComplete')
      this.$store.dispatch('hideSavedSearchModal')
    },
    handleClose() {
      this.showSaveSearchModal = false
      this.$store.dispatch('hideSavedSearchModal')
    },
  },
}
</script>
